<template>
  <main class="role-list-page">
    <section class="role-list-page__body">
      <router-link
        v-bind:to="{ name: 'ClinicPanelCabinetList' }"
        class="role-list-page__button-back"
      >
        Назад
      </router-link>
      <header class="role-list-page__body-header">
        <h1 class="role-list-page__title">
          <span>
            Роли сотрудников
          </span>
          <button
            type="button"
            v-on:click="showHintDialog = true"
          >
            ? Как настроить
          </button>
        </h1>
        <next-step-setting-instruction
          v-bind:current-route-name="$route.name"
          v-bind:available-steps="instructionSteps"
        >
          <button
            id="role-button-create-parent"
            class="role-list-page__add"
            title="Создать роль"
            type="button"
            v-if="canAddRole"
            v-on:click="toAddingRole"
          >Новая роль</button>
        </next-step-setting-instruction>
      </header>
      <div class="role-table">
        <div class="role-table__head">
          <div class="role-table__cell">№</div>
          <div class="role-table__cell">Название</div>
          <div class="role-table__cell" />
          <div class="role-table__cell" />
        </div>
        <template
          v-for="{
            canBeEdit,
            isDefault,
            id,
            name,
          } in roleList"
        >
          <button
            class="role-table__row"
            type="button"
            v-bind:key="id"
            v-on:click="selectRole(id)"
          >
            <span data-label="№" class="role-table__cell number" />
            <span data-label="Название" class="role-table__cell">{{ name }}</span>
            <template v-if="canBeEdit">
              <span
                data-label="Редактировать"
                class="role-table__cell"
              >
                <button
                  title="Отредактировать роль"
                  type="button"
                  v-on:click="selectRole(id)">
                  <span class="role-table__icon-edit" />
                </button>
              </span>
              <span
                data-label="Удалить"
                class="role-table__cell"
              >
                <button
                  title="Удалить роль"
                  type="button"
                  v-on:click.stop="onRoleRemove(id)">
                  <span class="role-table__icon-remove" />
                </button>
              </span>
            </template>
            <template v-else>
              <span
                data-label="Показать"
                class="role-table__cell"
                v-if="isDefault"
              >
                <button
                  title="Показать роль"
                  type="button"
                  v-on:click="selectRole(id)">
                  <span class="role-table__icon-view" />
                </button>
              </span>
            </template>
          </button>
        </template>
      </div>
    </section>
    <side-slide-transition>
      <template v-if="selectedRole">
        <side-dialog
          label="Просмотр роли"
          v-if="selectedRole.isDefault"
          v-click-outside="closeDialogForm"
          v-on:cancel="closeDialogForm">
          <dl class="role-definition">
            <div class="role-definition__row">
              <dt class="role-definition__term">Название:</dt>
              <dd class="role-definition__desc">{{ selectedRole.name }}</dd>
            </div>
            <dt class="role-definition__term">Права:</dt>
            <dd class="role-definition__desc">
              <ol>
                <li
                  v-for="{ name, permissions } in selectedRole.groupedPermissions"
                  v-bind:key="name">
                  {{ name }}
                  <ul class="role-definition__list">
                    <li
                      v-for="name in permissions"
                      v-bind:key="name">{{ name }}</li>
                  </ul>
                </li>
              </ol>
            </dd>
          </dl>
        </side-dialog>
        <confirm-side-dialog
          label="Редактирование роли"
          v-bind:need-confirm="needsConfirmOnCloseDialog"
          v-else
          v-on:cancel="closeDialogForm">
          <form-employee-role
            v-bind:initial-name="selectedRole.name"
            v-bind:initial-permissions="selectedRole.permissions"
            v-bind:permissions-by-groups="permissions"
            v-on:cancel="closeDialogForm"
            v-on:change-values="toggleConfirmation"
            v-on:submit="onEditRole" />
        </confirm-side-dialog>
      </template>
      <template v-if="hasRoleAdding">
        <confirm-side-dialog
          label="Новая роль"
          v-bind:need-confirm="needsConfirmOnCloseDialog"
          v-on:cancel="closeDialogForm">
          <form-employee-role
            v-bind:permissions-by-groups="permissions"
            v-on:change-values="toggleConfirmation"
            v-on:cancel="closeDialogForm"
            v-on:submit="onAddRole" />
        </confirm-side-dialog>
      </template>
      <assigned-employees-removing-error-dialog
        v-bind:bindings="assignedEmployeesRemovingError.bindings"
        v-bind:title="assignedEmployeesRemovingError.title"
        v-if="assignedEmployeesRemovingError"
        v-on:input="assignedEmployeesRemovingError = null" />
    </side-slide-transition>
    <confirm-side-dialog
      label="Как создать роль"
      v-if="showHintDialog"
      v-on:cancel="showHintDialog = false"
      class="role-list-page__modal"
    >
      <br>
      <p>
        С помощью раздела «Роли» владелец может
        наделить сотрудника нужными правами доступа к системе.
      </p>
      <p>
        После регистрации в программе доступны 3 роли
        с предустановленным набором прав и исключений:
        "Врач", "Администратор", "Ассистент". Данные роли
        не могут быть отредактированы или удалены.
        Чтобы просмотреть права для каждой роли,
        нажмите на нее левой кнопкой мыши.
      </p>
      <p>
        Чтобы создать новую роль, нажмите на кнопку «+Новая роль».
        Введите название и настройте необходимые права доступа.
        По готовности нажмите кнопку «Сохранить».
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/new/role.png')"
        alt="Как создать роль"
        class="role-list-page__modal__img"
      />
      <p>
        Созданную вручную роль можно редактировать или удалить.
      </p>
      <p class="role-list-page__modal__info-block">
        Удаление доступно только в&nbsp;случае, если роль
        не&nbsp;привязана к&nbsp;сотрудникам клиники.
        Всего может быть 4 роли, включая предустановленные.
      </p>
    </confirm-side-dialog>
    <modal-dialog
      v-bind:title="roleAddedTitle"
      v-bind:value="showModalRoleAdded"
      @input="showModalRoleAdded = false"
    >
      <div class="role-list-page__role-added-modal">
        <router-link
          class="role-list-page__role-added-modal__btn"
          v-bind:to="{ name: 'ClinicPanelEmployeeList' }"
        > Перейти в раздел Сотрудники</router-link>
      </div>
    </modal-dialog>
    <on-boarding-dialog
      :is-page-ready="isReadyToOnBoarding"
      :is-show-button-create-role="isShowButtonCreateRole"
      @showed="showedOnboarding"
    >
      <button
        v-if="isShowButtonCreateRole"
        id="role-button-create-child"
        class="role-list-page__show"
        title="Создать роль"
        type="button"
        v-on:click="toAddingRole(); closeAndUploadOnBoardingDialog('role-button-create')"
      >Новая роль</button>
    </on-boarding-dialog>
  </main>
</template>
<script>
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormEmployeeRole from '@/components/FormEmployeeRole.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import notifierMixin from '@/mixins/notifierMixin';
import settingInstructionMixin from '@/mixins/settingInstructionMixin';
import SideDialog from '@/components/SideDialog.vue';
import { RouterLink } from 'vue-router';
import ModalDialog from '@/components/ModalDialog.vue';
import NextStepSettingInstruction from '@/components/NextStepSettingInstruction.vue';
import ZoomImage from '@/components/ZoomImage.vue';
import onBoardingMixin from '@/mixins/onBoardingMixin';
import OnBoardingDialog from '@/components/OnBoardingDialog.vue';
import onBoardingShowMixin from '@/mixins/onBoardingShowMixin';

/**
 * @description Диалог ошибки удаления роли с назначенными ей сотрудниками.
 * @type {Vue}
 */
const AssignedEmployeesRemovingErrorDialog = {
  functional: true,
  name: 'AssignedEmployeesRemovingErrorDialog',
  render(createElement, { listeners, props: { bindings, title } }) {
    const createTextElement = (content) => createElement('span', content);
    const separator = ', ';
    const { prefix, names, postfix } = title
      .match(/^(?<prefix>[^[]+)(?<names>[^\]]+])(?<postfix>.+)$/)
      .groups;
    const nameElements = names
      .slice(1, names.length - 1)
      .split(separator)
      .flatMap((name, index, { length }) => {
        const isLastElement = length === (index + 1);
        const to = {
          params: { selectedEmployeeId: bindings[index] },
          name: 'ClinicPanelEmployeeList',
        };
        const linkElement = createElement(RouterLink, { props: { to } }, name);
        if (isLastElement) {
          return [linkElement];
        }
        const separatorElement = createTextElement(separator);
        return [linkElement, separatorElement];
      });
    return createElement(
      ModalDialog,
      {
        on: listeners,
        props: {
          title: 'Не удалось удалить роль',
          value: true,
        },
      },
      [
        createTextElement(prefix),
        ...nameElements,
        createTextElement(postfix),
      ],
    );
  },
};

/**
 * @description Раздел ролей сотрудников.
 * @type {Vue}
 */
export default {
  components: {
    OnBoardingDialog,
    ZoomImage,
    ModalDialog,
    NextStepSettingInstruction,
    AssignedEmployeesRemovingErrorDialog,
    SideDialog,
    ConfirmSideDialog,
    FormEmployeeRole,
    SideSlideTransition,
  },
  computed: {
    /**
     * Готова ли страница к показу ОнБординга
     *
     * @return {boolean}
     */
    isReadyToOnBoarding() {
      return this.canAddRole;
    },
    /**
     * Показываем представление "Создать роль"
     *
     * @return {boolean}
     */
    isShowButtonCreateRole() {
      return this.step.notShowedView === 'role-button-create';
    },
    /**
     * @description Выбранная роль
     * @return {*&{groupedPermissions: *}}
     */
    selectedRole() {
      return this
        .roleList
        .filter(({ id }) => id === this.selectedRoleId)
        .map((role) => {
          const groupedPermissions = this.permissions.flatMap(({ name, permissions }) => {
            const rolePermissions = permissions.flatMap(
              (item) => (role.permissions.includes(item.slug) ? [item.name] : []),
            );
            if (rolePermissions.length !== 0) {
              return [{ name, permissions: rolePermissions }];
            }
            return [];
          });
          return {
            ...role,
            groupedPermissions,
          };
        })
        .at(0);
    },
  },
  async created() {
    this.reloadRoleList();
    this.fetchDataForCreation();
    this.fetchInstructionSteps();
  },
  updated() {
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonCreateRole,
      '#role-button-create-parent',
      '#role-button-create-child',
    );
  },
  data() {
    return {
      assignedEmployeesRemovingError: null,
      canAddRole: false,
      hasRoleAdding: false,
      needsConfirmOnCloseDialog: false,
      permissions: [],
      selectedRoleId: null,
      roleList: [],
      /**
       * @description Показывать диалоговое окно подсказки.
       * @type {boolean}
       */
      showHintDialog: false,
      /**
       * @description Показывать диалоговое окно успешного создания роли.
       * @type {boolean}
       */
      showModalRoleAdded: false,
      /**
       * @description Титул диалогового окна успешного создания роли.
       * @type {string}
       */
      roleAddedTitle: '',
      /**
       * Слаг шага ОнБординга
       * @type {string}
       */
      stepSlug: 'meet-with-allowed-roles',
    };
  },
  methods: {
    closeDialogForm() {
      this.hasRoleAdding = false;
      this.selectedRoleId = null;
    },
    fetchDataForCreation() {
      this
        .$employeeRoleGateway
        .getDataForCreation()
        .then((result) => {
          result.forEach(({ groupedPermissions }) => {
            this.permissions = groupedPermissions;
          });
        });
    },
    onAddRole(fields) {
      this
        .$employeeRoleGateway
        .create(fields)
        .then((result) => {
          result.forEach(({ title }) => {
            this.reloadRoleList();
            this.closeDialogForm();
            this.notifySuccess(title);
            this.roleAddedTitle = `Теперь вы можете присвоить сотрудникам роль "${fields.name}"`;
            this.showModalRoleAdded = true;
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    onEditRole(fields) {
      this
        .$employeeRoleGateway
        .edit(this.selectedRole.id)(fields)
        .then((result) => {
          result.forEach(({ title }) => {
            this.reloadRoleList();
            this.closeDialogForm();
            this.notifySuccess(title);
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    async onRoleRemove(roleId) {
      const dialog = await this.showRemoveDialog(
        'Вы действительно хотите удалить роль?',
      );
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const result = await this.$employeeRoleGateway.remove(roleId);
      result.forEach(({ title }) => {
        this.reloadRoleList();
        this.notifySuccess(title);
      });
      result.forEachLeft(({ employee_bindings: bindings = {}, title }) => {
        if (0 in bindings) {
          this.assignedEmployeesRemovingError = { bindings, title };
        } else {
          this.notifyFail(title);
        }
      });
    },
    /**
     * Закрываем и обновляем данные о показе
     * ОнБординг представлений
     *
     * @param key
     *
     * @return {void}
     */
    closeAndUploadOnBoardingDialog(key) {
      this.uploadShowsUser(key);
      this.$refs['on-boarding-dialog'].deletePreparedWindow(key);
    },
    reloadRoleList() {
      this.$employeeRoleGateway.getList().then((result) => {
        result.forEach(({ canAddRole, items }) => {
          this.canAddRole = canAddRole;
          this.roleList = items;
        });
        const { title, body } = result.val.onBoardingStatus;
        this.checkAndUpdate(title, body);
      });
    },
    selectRole(roleId) {
      this.selectedRoleId = roleId;
    },
    /**
     * @description Перейти к добавлению роли.
     * @returns {void}
     */
    toAddingRole() {
      this.hasRoleAdding = true;
    },
    toggleConfirmation(name, isChanged) {
      this.needsConfirmOnCloseDialog = isChanged;
    },
  },
  mixins: [
    onBoardingMixin,
    onBoardingShowMixin,
    builtInDialogMixin,
    notifierMixin,
    settingInstructionMixin,
  ],
  name: 'ClinicPanelEmployeeRoleList',
};
</script>
<style lang="scss">
@use "@/scss/buttons";
.role {
  &-definition {
    margin: 42px 0 0 0;
    &__desc {
      font-size: 16px;
      margin: 0 0 20px 0;
      font-style: normal;
    }
    &__term {
      color: #192A3E;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 10px 0;
    }
    &__row {
      display: flex;
      flex-flow: row wrap;
      column-gap: 10px;
      row-gap: 5px;
      align-items: center;
      margin: 0 0 10px 0;
      .role-definition__term,
      .role-definition__desc {
        margin: 0;
      }
    }
    &__list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      list-style: none;
      padding: 0 0 0 10px !important;
      margin: 5px 0 10px 0;
      li {
        display: flex;
        align-items: center;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        user-select: none;
        &:before {
          content: '';
          display: inline-block;
          flex-grow: 0;
          flex-shrink: 0;
          height: 20px;
          width: 20px;
          margin: 0 5px 0 0;
          background-color: var(--unfocus-color);
          background-position: center center;
          background-repeat: no-repeat;
          background-size: 70%;
          background-image: url("@/assets/img/checked/checked-white-top.svg");
          border: 1px solid var(--unfocus-color);
          border-radius: 4px;
        }

      }
    }
  }
}

.role-list-page {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  &__title {
    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
    gap: 10px 30px;
    button {
      font-size: 18px;
      font-weight: 500;
      color: #90a0b7;
      &:hover {
        color: #0489E0;
      }
    }
  }
  &__modal {
    &__img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
      margin: 15px 0;
    }
    &__info-block {
      position: relative;
      display: flex;
      align-items: center;
      margin-block: 15px;
      padding: 1rem;
      border-left: 50px solid var(--action-color);
      border-radius: .5rem 0 0 .5rem;
      background-color: #e3f2fd;
      font-size: 14px;
      color: #0a59a4;
      &::before {
        content: 'i';
        position: absolute;
        top: 50%;
        left: -35px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        font-size: 14px;
        background-color: #e3f2fd;
        border-radius: 50%;
        transform: translateY(-50%);
      }
    }
    p {
      margin-bottom: 10px !important;
    }
  }
  &__role-added-modal {
    display: flex;
    justify-content: center;
    &__btn {
      @include buttons.btn-transparent(250px, 48px, 16px);
      text-decoration: none;
    }
  }
  &__button-back {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    text-decoration: none;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
}
.role-list-page .role-list-page__body {
  overflow-y: scroll;
  padding: 38px 20px 48px 23px;
  width: 100%;
}
.role-list-page .role-list-page__body-header {
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
  justify-content: space-between;
}
.role-list-page__show {
  width:250px;
  height: 150px;
  @include buttons.btn-blue(250px, 48px, 16px);
  @include buttons.btn-blue-add(19px);
}

.role-list-page .role-list-page__add {
  @include buttons.btn-blue(250px, 48px, 16px);
  @include buttons.btn-blue-add(19px);
}

.role-list-page .role-list-page__button-child {
  @include buttons.btn-blue(162px, 32px, 16px);
  text-decoration: none;
  color: #FFFFFF !important;
}
.role-table {
  counter-reset: roleCounter;
  margin: 35px 0 0 0;
}
.role-table .role-table__head,
.role-table .role-table__row {
  width: 100%;
  text-align: left;
  display: grid;
  grid-template-columns: 40px 1fr repeat(2, 43px);
  align-items: center;
  font-size: 14px;
  line-height: 17px;
}
.role-table .role-table__head {
  border-bottom: 2px solid #0489e0;
  font-weight: 700;
  padding: 10px 0;
}
.role-table .role-table__row {
  border-bottom: 1px solid var(--fill-color);
  cursor: pointer;
  padding: 12px 0;
}
.role-table .role-table__row:hover {
  background: var(--fill-color);
}
.role-table .role-table__cell {
  padding: 0 8px;
}
.role-table .role-table__cell.number::after {
  counter-increment: roleCounter;
  content: counter(roleCounter);
}
.role-table .role-table__icon-edit,
.role-table .role-table__icon-remove,
.role-table .role-table__icon-view {
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;
  display: block;
}
.role-table .role-table__icon-edit {
  background-image: url("@/assets/img/pen-grey.svg");
  height: 14px;
  width: 14px;
}
.role-table .role-table__icon-edit:hover {
  background-image: url("@/assets/img/pen-blue.svg");
}
.role-table .role-table__icon-remove {
  background-image: url("@/assets/img/exit-grey.svg");
  height: 12px;
  width: 12px;
}
.role-table .role-table__icon-remove:hover {
  background-image: url("@/assets/img/exit-blue.svg");
}
.role-table .role-table__icon-view {
  background-image: url("@/assets/img/eye-open.svg");
  height: 18px;
  width: 18px;
}
@media screen and (max-width: 640px) {
  .role-list-page .role-list-page__body {
    padding: 38px 10px 140px;
  }
}
@media screen and (max-width: 480px) {
  .role-table .role-table__head {
    display: none;
  }
  .role-table .role-table__row {
    grid-gap: 10px;
    grid-template-columns: 1fr;
    margin: 0 0 20px 0;
  }
  .role-table .role-table__cell {
    display: flex;
    justify-content: space-between;
    text-align: right;
  }
  .role-table .role-table__cell:before {
    content: attr(data-label);
    font-weight: 700;
    margin-right: 20px;
  }
}
</style>
